document.addEventListener('DOMContentLoaded', () => {
  const SWAP_INTERVAL_MS = 2300;
  const FADE_MS = 550;

  const getVisibleCount = () => {
    const width = window.innerWidth;
    if (width <= 900) return 4;
    if (width <= 1024) return 5;
    if (width <= 1280) return 7;
    return 9;
  };

  const blocks = document.querySelectorAll('.client-logos-bar--shuffle');
  blocks.forEach((bar) => {
    const items = Array.from(bar.querySelectorAll('.client-logos-bar__item'));
    if (items.length <= getVisibleCount()) {
      return;
    }

    let timer = null;

    // Pick the best URL from a srcset string for the current viewport/DPR
    // so every preload and every swap use the exact same URL.
    const resolveBestSrc = (src, srcset) => {
      if (!srcset) return src;
      const dpr = window.devicePixelRatio || 1;
      const slotPx = Math.ceil((window.innerWidth / getVisibleCount()) * dpr);
      const candidates = srcset
        .split(',')
        .map((entry) => {
          const parts = entry.trim().split(/\s+/);
          return { url: parts[0], w: parseInt(parts[1]) || 0 };
        })
        .filter((c) => c.url && c.w > 0)
        .sort((a, b) => a.w - b.w);
      if (!candidates.length) return src;
      return (candidates.find((c) => c.w >= slotPx) || candidates[candidates.length - 1]).url;
    };

    const seenSrcs = new Set();
    const logos = items
      .map((item) => {
        const img = item.querySelector('img');
        if (!img) return null;
        const src = img.getAttribute('src') || '';
        const srcset = img.getAttribute('srcset') || '';
        return {
          src,
          resolvedSrc: resolveBestSrc(src, srcset),
          alt: img.getAttribute('alt') || '',
          weight: parseInt(item.getAttribute('data-weight') || '1', 10) || 1,
        };
      })
      .filter(Boolean)
      .filter((logo) => {
        if (!logo.src || seenSrcs.has(logo.src)) return false;
        seenSrcs.add(logo.src);
        return true;
      });

    const maxWeight = logos.reduce((m, l) => Math.max(m, l.weight), 1);

    // Inject preload <img> elements into a hidden-but-rendered container.
    // JS-created elements bypass WordPress's lazy-loading filter entirely.
    // High-weight logos get fetchpriority="high" so they finish first.
    const preloadBin = document.createElement('div');
    preloadBin.setAttribute('aria-hidden', 'true');
    preloadBin.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden;pointer-events:none;';
    bar.appendChild(preloadBin);

    const preloadedImages = new Map();
    logos.forEach((logo) => {
      const img = document.createElement('img');
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', logo.weight >= maxWeight ? 'high' : 'low');
      img.setAttribute('alt', '');
      img.src = logo.resolvedSrc;
      preloadBin.appendChild(img);
      preloadedImages.set(logo.src, img);
    });

    // slotLogo tracks which logo object lives in each visible slot index.
    // This avoids reading the DOM src (which mixes original src and resolvedSrc).
    const logoByUrl = new Map();
    logos.forEach((l) => {
      logoByUrl.set(l.src, l);
      if (l.resolvedSrc !== l.src) logoByUrl.set(l.resolvedSrc, l);
    });

    const slotLogo = new Map();
    const initSlotLogo = (visibleCount) => {
      items.slice(0, visibleCount).forEach((item, i) => {
        if (slotLogo.has(i)) return;
        const domSrc = item.querySelector('img')?.getAttribute('src') || '';
        const logo = logoByUrl.get(domSrc);
        if (logo) slotLogo.set(i, logo);
      });
    };
    initSlotLogo(getVisibleCount());

    const DWELL_MS = FADE_MS * 2 + 200;
    const slotLastSwapped = new Map();

    // Shuffled round-robin queue of slot indices.
    // Every slot swaps exactly once per cycle before any slot repeats.
    let slotQueue = [];
    let lastVisibleCount = 0;
    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    const isLoaded = (logo) => {
      const img = preloadedImages.get(logo.src);
      return img ? img.complete : true;
    };

    const pickIncomingLogo = (visibleCount) => {
      const visibleLogoSrcs = new Set();
      for (let i = 0; i < visibleCount; i++) {
        const l = slotLogo.get(i);
        if (l) visibleLogoSrcs.add(l.src);
      }
      const ready = logos.filter(isLoaded);
      if (!ready.length) return null;

      const readyWeight = ready.reduce((s, l) => s + l.weight, 0);
      for (let attempt = 0; attempt < 8; attempt++) {
        let roll = Math.random() * readyWeight;
        for (const logo of ready) {
          roll -= logo.weight;
          if (roll <= 0) {
            if (!visibleLogoSrcs.has(logo.src)) return logo;
            break; // already visible — retry
          }
        }
      }
      // Fallback: weighted random from ready logos not currently visible
      const hidden = ready.filter((l) => !visibleLogoSrcs.has(l.src));
      if (!hidden.length) return null;
      const hw = hidden.reduce((s, l) => s + l.weight, 0);
      let roll = Math.random() * hw;
      for (const logo of hidden) {
        roll -= logo.weight;
        if (roll <= 0) return logo;
      }
      return hidden[hidden.length - 1];
    };

    const nextSlotIndex = (visibleCount) => {
      // Rebuild queue when exhausted or when visible count changed
      // (either direction — shrinking leaves stale indices, growing leaves
      // newly-visible slots out of rotation until the queue exhausts).
      if (!slotQueue.length || visibleCount !== lastVisibleCount) {
        slotQueue = shuffle(Array.from({ length: visibleCount }, (_, i) => i));
        lastVisibleCount = visibleCount;
      }
      // Find the next slot in queue that isn't mid-transition
      const now = Date.now();
      for (let i = slotQueue.length - 1; i >= 0; i--) {
        const idx = slotQueue[i];
        if (now - (slotLastSwapped.get(idx) || 0) >= DWELL_MS) {
          slotQueue.splice(i, 1);
          return idx;
        }
      }
      return -1;
    };

    const swapRandomLogo = () => {
      const visibleCount = getVisibleCount();
      initSlotLogo(visibleCount);
      const visibleItems = items.slice(0, visibleCount);
      if (!visibleItems.length) return;

      const incomingLogo = pickIncomingLogo(visibleCount);
      if (!incomingLogo) return;

      const visibleIndex = nextSlotIndex(visibleCount);
      if (visibleIndex < 0) return;

      const visibleItem = visibleItems[visibleIndex];
      const visibleImg = visibleItem.querySelector('img');
      if (!visibleImg || !incomingLogo.resolvedSrc) return;

      slotLogo.set(visibleIndex, incomingLogo);
      slotLastSwapped.set(visibleIndex, Date.now());
      visibleImg.classList.add('is-fading');

      let swapped = false;
      const doSwap = () => {
        if (swapped) return;
        swapped = true;
        visibleImg.removeEventListener('transitionend', doSwap);

        // Drop srcset/sizes BEFORE changing src so the browser uses our exact
        // URL rather than re-picking from the stale srcset on the first swap.
        visibleImg.removeAttribute('srcset');
        visibleImg.removeAttribute('sizes');
        visibleImg.setAttribute('src', incomingLogo.resolvedSrc);
        visibleImg.setAttribute('alt', incomingLogo.alt);

        // The new bitmap is already in cache from the preload bin, so we just
        // give the browser one paint frame to commit the new src at opacity:0
        // before starting the fade-in. (Don't use img.decode() here — its
        // promise can hang on cached images in some browsers, which would
        // leave the slot permanently stuck at opacity:0 and prevent further
        // visible swaps on that slot.)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            visibleImg.classList.remove('is-fading');
          });
        });
      };
      visibleImg.addEventListener('transitionend', doSwap);
      // Safety: fire slightly later than transitionend so the event almost
      // always wins under load (avoids restarting opacity mid-fade-out).
      window.setTimeout(doSwap, FADE_MS + 200);
    };

    const start = () => {
      if (timer) return;
      if (logos.length > getVisibleCount()) {
        timer = window.setInterval(swapRandomLogo, SWAP_INTERVAL_MS);
      }
    };

    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    // Don't start swapping until ALL preloaded images have finished downloading.
    // This prevents low-weight logos from appearing early just because their
    // files happened to download before the high-weight ones.
    const tryStart = () => {
      if (logos.every(isLoaded)) start();
    };

    logos.forEach((logo) => {
      const img = preloadedImages.get(logo.src);
      if (!img || img.complete) {
        tryStart();
        return;
      }
      img.addEventListener('load', tryStart, { once: true });
      img.addEventListener('error', tryStart, { once: true });
    });
    tryStart(); // in case everything was already cached

    // Safety valve: start after 8 s regardless so a slow/failing image
    // never permanently blocks the animation.
    window.setTimeout(start, 8000);

    window.addEventListener('resize', () => {
      stop();
      start();
    });
  });
});
